import type { AuthModel } from 'pocketbase';
import { v4 as uuidv4 } from 'uuid';

export const useDataStore = defineStore('data', () => {
  const DB = useDbStore();
  const products = useProductsStore();

  const user = ref<UserI | null>(null);

  const orders = ref<Record<string, OrderI>>({});

  const addresses = ref<Record<string, AddressI>>({});
  const bonuses = ref<Record<string, BonusI>>({});
  const referrals = ref<Record<string, ReferralI>>({});
  const customizerPresets = ref<Record<string, CustomizerPresetI>>({});

  const referralList = computed(() => {
    return Object.values(referrals.value);
  });

  const showReferralNotification = computed(() => {
    return user.value?.showReferralNotification || false;
  });

  const bonusAmount = computed(() => {
    return Object.values(bonuses.value).reduce((acc, bonus) => {
      return acc + bonus.amount;
    }, 0);
  });

  const localOrders = useLocalStorage<Record<string, OrderI>>('orders', {});

  // cleans files empty objects after reload
  Object.values(localOrders.value).forEach((order) => {
    order.files = [];
  });

  const ordersPromises: Map<string, Promise<ServerOrderI | null>> = new Map();

  const myOrders = computed(() => {
    return Object.values(orders.value).filter(order => order.status !== 'cart' && order.status !== 'closed');
  });

  const historyOrders = computed(() => {
    return Object.values(orders.value).filter(order => order.status !== 'cart');
  });

  const preCartOrder = computed(() => {
    return Object.values(orders.value).filter(order => order.status === 'precart')[0] || {
      patient: '',
      files: [],
      id: '',
      comment: '',
    };
  });

  const cartOrders = computed(() => {
    return Object.values(orders.value).filter(order => order.status === 'cart');
  });
  const forApproveOrders = computed(() => {
    return Object.values(orders.value).filter(order => order.status === 'forapprove');
  });

  const ordersWithSubscriptions: Set<string> = new Set();

  const addressessWithSubscriptions: Set<string> = new Set();

  const bonusesWithSubscriptions: Set<string> = new Set();

  const costumizerPresetsWithSubscriptions: Set<string> = new Set();

  // const list = computed(() => {
  //   return Array.from(orders.value.values());
  // });
  // // const

  const setUser = (model: UserI | AuthModel) => {
    if (!model) {
      return;
    }
    user.value = {
      avatar: model.avatar,
      email: model.email,
      name: model.name,
      verified: model.verified,
      deliveryDetails: model.deliveryDetails,
      billingDetails: model.billingDetails,
      phone: model.phone,
      cash: model.cash,
      id: model.id,
      loyaltyDiscount: model.loyaltyDiscount || 0,
      bonuses: model.bonuses,
      isReferral: model.isReferral || false,
      referralList: model.referralList || [],
      customizerPresets: model.customizerPresets || [],
      showReferralNotification: model.showReferralNotification || false,
      freeDeliveryNotification: model.freeDeliveryNotification || false,
    };
  };

  const setOrder = async (order: ServerOrderI) => {
    // TODO replace OrderI with ServerOrderI
    orders.value[order.id] = products.convertServerOrderToOrder(order);
  };

  const setAddress = (address: AddressI) => {
    addresses.value[address.id] = address;
  };

  const setReferral = (referral: ReferralI) => {
    referrals.value[referral.id] = referral;
  };

  const setBonus = (bonus: BonusI) => {
    bonuses.value[bonus.id] = bonus;
  };

  const setCustomizerPreset = (preset: CustomizerPresetI) => {
    customizerPresets.value[preset.id] = preset;
  };

  const deleteAddress = (address: AddressI) => {
    delete addresses.value[address.id];
  };

  const deleteCustomizerPreset = (preset: CustomizerPresetI) => {
    delete customizerPresets.value[preset.id];
  };

  // const setReferralList = (referrals: ReferralI[]) => {

  const subscribeOnUser = (id: string) => {
    // console.log(id);
    DB.subscribeToCollection('users', (data) => {
      // console.log(data.action, data.record);

      setUser(data.record);
      orders.value = {};
      data.record?.expand?.orders?.forEach((order) => {
        setOrder(order);
        subscribeOnItem(order.id);
      });
      data.record?.expand?.deliveryList?.forEach((address: AddressI) => {
        setAddress(address);
        subscribeOnAddress(address.id);
      });
      // console.log(data.record);
      data.record?.expand?.bonuses?.forEach((bonus: BonusI) => {
        setBonus(bonus);
        subscribeOnBonus(bonus.id);
      });
      data.record?.expand?.referralList?.forEach((referral: ReferralI) => {
        setReferral(referral);
      });

      data.record?.expand?.customizerPresets?.forEach((preset: CustomizerPresetI) => {
        setCustomizerPreset(preset);
        subscribeOnCustomizerPreset(preset.id);
      });
      //
    }, id, { expand: 'orders,deliveryList,referralList,bonuses,bonuses.user,customizerPresets' });
  };

  const subscribeOnAddress = (id: string) => {
    // console.log('subscribeOnAddress', id);
    if (addressessWithSubscriptions.has(id)) return;
    addressessWithSubscriptions.add(id);
    // console.log(id);
    DB.subscribeToCollection(
      'deliveryDetails',
      ({ action, record }) => {
        // console.log(action, record);
        if (action === 'update') {
          setAddress(record);
        }
        if (action === 'delete') {
          deleteAddress(record);
        }
      },
      id,
      { expand: 'user' },
    );
  };

  const subscribeOnBonus = (id: string) => {
    // console.log('subscribeOnBonus', id);
    if (bonusesWithSubscriptions.has(id)) return;
    bonusesWithSubscriptions.add(id);
    // console.log(id);
    DB.subscribeToCollection(
      'bonuses',
      ({ action, record }) => {
        // console.log(action, record);
        if (action === 'update') {
          setBonus(record);
        }
      },
      id,
      { expand: 'user' },
    );
  };

  const subscribeOnItem = (id: string) => {
    // console.log('subscribeOnItem', id);
    if (ordersWithSubscriptions.has(id)) return;
    ordersWithSubscriptions.add(id);
    // console.log(id);
    DB.subscribeToCollection(
      'orders',
      ({ action, record }) => {
        // console.log(action, record);
        if (action === 'update') {
          setOrder(record);
        }
      },
      id,
      { expand: 'user' },
    );
  };

  const subscribeOnCustomizerPreset = (id: string) => {
    // console.log('subscribeOnCustomizerPreset', id);
    if (costumizerPresetsWithSubscriptions.has(id)) return;
    costumizerPresetsWithSubscriptions.add(id);
    // console.log(id);
    DB.subscribeToCollection(
      'customizerPresets',
      ({ action, record }) => {
        // console.log(action, record);
        if (action === 'update') {
          setCustomizerPreset(record);
        }
        if (action === 'delete') {
          deleteCustomizerPreset(record);
        }
      },
      id,
      { expand: 'user' },
    );
  };

  const initiateUserData = (model) => {
    if (model) {
      setUser(model);
      subscribeOnUser(model.id);

      model?.expand?.orders?.forEach((order: ServerOrderI) => {
        setOrder(order);
        subscribeOnItem(order.id);
      });

      model?.expand?.deliveryList?.forEach((address: AddressI) => {
        setAddress(address);
        subscribeOnAddress(address.id);
      });

      // console.log(model);

      model?.expand?.bonuses?.forEach((bonus: BonusI) => {
        setBonus(bonus);
        subscribeOnBonus(bonus.id);
      });

      model?.expand?.referralList?.forEach((referral: ReferralI) => {
        setReferral(referral);
      });

      model?.expand?.customizerPresets?.forEach((preset: CustomizerPresetI) => {
        setCustomizerPreset(preset);
        subscribeOnCustomizerPreset(preset.id);
      });
    }
  };

  initiateUserData(DB.auth.model);

  const createOrder = async (item: ItemI, options: Partial<ServerOrderI> = {}) => {
    const localId = uuidv4();

    console.log(item);

    const stickers = [...item.defaultStickers].map((sticker) => {
      return {
        ...sticker,
        position: sticker.position || { x: 0, y: 0 },
        scale: sticker.scale || { x: 1, y: 1 },
        rotation: sticker.rotation || 0,
      };
    });

    stickers.forEach((sticker) => {
      // TODO: VALIDATE DATA FROM JSON/SERVER (IT CAN BE MISMATCHED !!!)
      sticker.instrumentType = 'sticker';
      sticker.scale = sticker.scale || { x: 1, y: 1 };
      sticker.rotation = sticker.rotation || 0;
      if (!Object.hasOwnProperty.call(sticker, 'flipX')) sticker.flipX = false;
    });

    const totalPrice = calculateOrderPrice({
      stickers,
      item,
    }, 0);
    const totalPriceAfterDiscount = calculateOrderPrice({
      stickers,
      item,
    }, user.value?.loyaltyDiscount);

    const order: ServerOrderI = {
      localId,
      itemName: item.name,
      itemType: item.type,
      customizer: {
        stickers,
        lines: [],
        pencils: [],
        comment: '',
      },
      comment: '',
      patient: '',
      file: null,
      files: [],
      approveFile: null,
      approveFiles: [],
      approveComments: { list: [] },
      approved: false,
      deliveryDetails: verifyDeliveryDetails(user?.deliveryDetails),
      billingDetails: verifyBillingDetails(user?.billingDetails),
      totalPrice,
      paid: false,
      discount: totalPrice - totalPriceAfterDiscount,
      status: 'cart',

      selectedInCart: true,
      user: user?.value?.id,
      ...options,
    };

    const isValid = await DB.checkValid();
    if (isValid) {
      const sOrderPromise = DB.createOrder(order)
        .then((sOrder) => {
          setOrder(sOrder);
          return sOrder;
        });

      ordersPromises.set(order.localId, sOrderPromise);
      return sOrderPromise;
    }

    await products.productFetchPromise;

    const localOrder = products.convertServerOrderToOrder(order);

    localOrders.value[order.localId] = localOrder;
    return localOrder;
  };

  const requestSyncOrder = async (order: OrderI) => {
    const o = convertOrderToServerOrder({ ...order, user: user.value?.id });

    const files = [...o.files];
    o.files.length = 0;

    const promise = DB.createOrder(o)
      .then((sOrder) => {
        delete localOrders.value[sOrder.localId];
        setOrder(sOrder);

        ordersPromises.delete(order.localId);

        if (files.length) {
          DB.addOrderFiles(sOrder.id, files);
        }
      });

    ordersPromises.set(order.localId, promise);
    return promise;
  };

  const syncOrdersWithDB = async () => {
    const keys = Object.keys(localOrders.value);
    const lId = keys[0];

    if (!lId) return;
    requestSyncOrder(localOrders.value[lId])
      .then(() => {
        syncOrdersWithDB();
      });
  };

  DB.onValidationChange((model) => {
    if (model) {
      initiateUserData(model);

      syncOrdersWithDB();
    }
    else {
      user.value = null;
      orders.value = {};
      addresses.value = {};
      DB.unsubscribeFromCollection('users');
      ordersWithSubscriptions.clear();
      addressessWithSubscriptions.clear();
      DB.unsubscribeFromCollection('orders');
      DB.unsubscribeFromCollection('deliveryDetails');
      DB.unsubscribeFromCollection('bonuses');
      DB.unsubscribeFromCollection('customizerPresets');
      bonusesWithSubscriptions.clear();
      costumizerPresetsWithSubscriptions.clear();
    }
  });

  return {
    user,
    orders,
    addresses,
    bonuses,
    bonusAmount,
    referralList,
    showReferralNotification,
    localOrders,

    customizerPresets,

    myOrders,
    preCartOrder,
    cartOrders,
    forApproveOrders,
    historyOrders,

    setOrder,
    createOrder,

    ordersPromises,
  };
});
